import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SizeFinderStrip from "@/components/SizeFinderStrip";
import { api, fileUrl } from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useFitRanking, RIBBON_THRESHOLD_CM } from "@/lib/fit";
import { useSettings } from "@/context/SettingsContext";
import { cmToInput, lengthLabel } from "@/lib/units";

export default function Shop() {
  const { settings } = useSettings();
  const unitSystem = settings?.unit_system || "metric";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [color, setColor] = useState("");
  const [activeFits, setActiveFits] = useState(() => new Set());
  const [activeSizes, setActiveSizes] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    api.get("/storefront/inventory")
      .then((r) => { if (!cancelled) setItems(r.data); })
      .catch((e) => {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("shop inventory load failed", e);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const types = useMemo(() => Array.from(new Set(items.map((i) => i.type).filter(Boolean))), [items]);
  const colors = useMemo(() => Array.from(new Set(items.map((i) => i.color).filter(Boolean))), [items]);

  const filtered = useMemo(() => items.filter((it) =>
    (!q || `${it.title} ${it.brand} ${it.fabric}`.toLowerCase().includes(q.toLowerCase())) &&
    (!type || it.type === type) &&
    (!color || it.color === color) &&
    (activeFits.size === 0 || activeFits.has(it.size_fit)) &&
    (activeSizes.size === 0 || activeSizes.has(it.size))
  ), [items, q, type, color, activeFits, activeSizes]);

  // Apply the visitor's saved fit profile (if any) to re-rank matches to the top.
  const { items: ranked, scores: fitScores, hasProfile, hasFitMatches } = useFitRanking(filtered);

  return (
    <div className="bg-ivory min-h-screen">
      <Navbar />
      <header className="max-w-7xl mx-auto px-8 lg:px-16 pt-20 pb-12" data-testid="shop-header">
        <span className="editorial-label">the edit · vol. i</span>
        <h1 className="font-serif text-5xl sm:text-6xl lowercase text-charcoal mt-3">curated with intention.</h1>
        <p className="mt-6 max-w-xl text-espresso leading-relaxed">each piece is sourced, restored, and photographed in our atelier. nothing here is mass-made.</p>
      </header>

      {/* Size Finder · pill bar derived from the live inventory */}
      <SizeFinderStrip
        items={items}
        activeFits={activeFits}
        activeSizes={activeSizes}
        onChangeFits={setActiveFits}
        onChangeSizes={setActiveSizes}
      />

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-8 lg:px-16 pb-12 grid md:grid-cols-4 gap-6 border-b border-taupe/40">
        <input
          data-testid="shop-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search the atelier…"
          className="bg-transparent border-b border-taupe py-3 focus:outline-none focus:border-brass placeholder:text-taupe text-charcoal"
        />
        <select data-testid="shop-filter-type" value={type} onChange={(e) => setType(e.target.value)} className="bg-transparent border-b border-taupe py-3 focus:outline-none focus:border-brass text-charcoal">
          <option value="">all types</option>
          {types.map((t) => <option key={t} value={t}>{t.toLowerCase()}</option>)}
        </select>
        <select data-testid="shop-filter-color" value={color} onChange={(e) => setColor(e.target.value)} className="bg-transparent border-b border-taupe py-3 focus:outline-none focus:border-brass text-charcoal">
          <option value="">all hues</option>
          {colors.map((c) => <option key={c} value={c}>{c.toLowerCase()}</option>)}
        </select>
        <div className="flex items-end editorial-label">{filtered.length} pieces</div>
      </div>

      <section className="max-w-7xl mx-auto px-8 lg:px-16 py-16">
        {hasProfile && fitScores.size > 0 && (
          <div className="mb-10 flex items-center gap-2 text-[0.7rem] tracking-widest uppercase text-brass" data-testid="fit-rank-banner">
            <BadgeCheck className="w-3.5 h-3.5" />
            {hasFitMatches
              ? "ranked by your saved fit profile · pieces closest to you first"
              : "ranked by your saved fit profile · nothing inside the snug-fit window yet"}
          </div>
        )}
        {filtered.length === 0 ? (
          <div className="text-center py-24 border border-taupe/40">
            <p className="font-serif text-3xl lowercase text-charcoal">no pieces match this story yet.</p>
            <p className="editorial-label mt-4">timeless over trendy.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-20">
            {ranked.map((it, idx) => {
              const fitScore = fitScores.get(it.id);
              const isYourFit = fitScore && fitScore.avgDelta <= RIBBON_THRESHOLD_CM;
              return (
                <Link key={it.id} to={`/product/${it.id}`} className="group block relative" data-testid={`shop-card-${idx}`}>
                  <div className="aspect-[3/4] bg-linen overflow-hidden mb-5 relative">
                    {it.photos?.[0] ? (
                      <img src={fileUrl(it.photos[0])} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><span className="font-serif text-xl lowercase text-taupe">awaiting studio</span></div>
                    )}
                    {isYourFit && (
                      <span
                        className="absolute top-3 left-3 inline-flex items-center gap-1 bg-brass text-ivory text-[0.55rem] tracking-widest uppercase px-2.5 py-1"
                        data-testid={`your-fit-ribbon-${idx}`}
                        title={`average delta · ${cmToInput(fitScore.avgDelta, unitSystem)} ${lengthLabel(unitSystem)} across ${fitScore.fieldCount} measurement${fitScore.fieldCount === 1 ? "" : "s"}`}
                      >
                        <BadgeCheck className="w-2.5 h-2.5" /> your fit
                      </span>
                    )}
                  </div>
                  <p className="editorial-label">{it.brand || "—"}</p>
                  <h3 className="font-serif text-2xl lowercase text-charcoal mt-1 group-hover:text-brass transition-colors">{it.title}</h3>
                  <p className="text-sm text-espresso mt-2 tracking-wide">
                    {it.original_price > 0 && it.original_price > it.sale_price ? (
                      <>
                        <span className="line-through text-taupe/70 mr-2">{formatMoney(it.original_price)}</span>
                        <span className="text-brass font-medium">{formatMoney(it.sale_price)}</span>
                        <span className="ml-2 text-[0.65rem] uppercase tracking-widest text-brass">-{it.markdown_pct}%</span>
                      </>
                    ) : (
                      formatMoney(it.sale_price)
                    )}
                  </p>
                </Link>
              );
            })}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
